import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { IonIcon } from '@ionic/angular/standalone';
import { filter, fromEvent, map, merge } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { PwaInstallService } from 'src/app/core/services/pwa/pwa-install.service';
import {
  ANDROID_PROMPT_INSTRUCTIONS,
  PwaInstallInstructions,
  getPwaInstallInstructions,
} from 'src/app/core/services/pwa/pwa-install.content';

/**
 * Tab roots where the automatic prompt may appear. Never on stack pages (mid-task), and the
 * component only lives in the signed-in user shell, so never on /welcome, /login, /signup etc.
 */
export const PWA_AUTO_PROMPT_ROUTES: readonly string[] = [
  '/main/home',
  '/main/discover',
  '/main/donate',
  '/main/sunny-drives',
  '/main/commerce',
];

/** The user has to settle on a tab root this long before the automatic prompt slides in. */
export const PWA_AUTO_PROMPT_DELAY_MS: number = 4000;

function stripQuery(url: string): string {
  return url.split(/[?#]/)[0];
}

/**
 * Is any modal other than `excluding` still presented? Ionic emits `ionModalDidDismiss` while the
 * dismissing modal still carries `show-modal`, so the event's own target has to be left out.
 */
function hasPresentedModal(excluding: EventTarget | null = null): boolean {
  const presented: Element[] = Array.from(document.querySelectorAll('ion-modal.show-modal'));
  return presented.some((modal: Element) => modal !== excluding);
}

/**
 * "Add to Home Screen" sheet for mobile-web users (mounted once in the user tabs shell).
 *
 * Automatic trigger: signed in, second visit or later (see PwaInstallService), sitting on a main
 * tab root for a few seconds with no modal open, not snoozed by "Not now" (14 days), not
 * installed. Once per page load at most. Settings opens it on demand via
 * `PwaInstallService.open('settings')`, which ignores the visit/snooze rules.
 */
@Component({
  selector: 'app-pwa-install-prompt',
  templateUrl: './pwa-install-prompt.component.html',
  styleUrls: ['./pwa-install-prompt.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonIcon, NgOptimizedImage],
})
export class PwaInstallPromptComponent {
  protected readonly pwaInstall = inject(PwaInstallService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly currentUrl = toSignal<string, string>(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  // Seeded from the DOM (a modal may already be up when the shell mounts), then kept current by
  // Ionic's modal events — the single source for "is a modal open?".
  private readonly isModalOpen = signal<boolean>(hasPresentedModal());
  private autoPromptTimer: ReturnType<typeof setTimeout> | null = null;
  private hasAutoPrompted: boolean = false;

  protected readonly isVisible = computed<boolean>(() => this.pwaInstall.isOpen() && !this.isModalOpen());
  protected readonly showNativeInstall = computed<boolean>(
    () => this.pwaInstall.platform() === 'android-chromium' && this.pwaInstall.canPromptNatively(),
  );
  protected readonly instructions = computed<PwaInstallInstructions | null>(() =>
    this.showNativeInstall() ? ANDROID_PROMPT_INSTRUCTIONS : getPwaInstallInstructions(this.pwaInstall.platform()),
  );
  protected readonly dismissLabel = computed<string>(() =>
    this.pwaInstall.openedFrom() === 'settings' ? 'Close' : 'Not now',
  );

  constructor() {
    merge(
      fromEvent(document, 'ionModalWillPresent').pipe(map(() => true)),
      fromEvent(document, 'ionModalDidDismiss').pipe(map((event: Event) => hasPresentedModal(event.target))),
    )
      .pipe(takeUntilDestroyed())
      .subscribe((isOpen: boolean) => this.isModalOpen.set(isOpen));

    effect(() => {
      const isCandidate: boolean =
        this.authService.currentUser() !== null &&
        PWA_AUTO_PROMPT_ROUTES.includes(stripQuery(this.currentUrl())) &&
        !this.isModalOpen();
      untracked(() => this.scheduleAutoPrompt(isCandidate));
    });

    // The automatic sheet belongs to the tab root it appeared on: leaving it (e.g. into a stack
    // page like /main/cart) closes it, without the 14-day snooze. A sheet opened from Settings
    // was asked for explicitly and stays until the user closes it.
    effect(() => {
      const isOnTabRoot: boolean = PWA_AUTO_PROMPT_ROUTES.includes(stripQuery(this.currentUrl()));
      if (!isOnTabRoot && this.pwaInstall.openedFrom() === 'auto') {
        untracked(() => this.pwaInstall.close());
      }
    });

    this.destroyRef.onDestroy(() => this.clearTimer());
  }

  protected install(): void {
    void this.pwaInstall.promptInstall();
  }

  protected dismiss(): void {
    this.pwaInstall.dismiss();
  }

  private scheduleAutoPrompt(isCandidate: boolean): void {
    this.clearTimer();
    if (!isCandidate || this.hasAutoPrompted || !this.pwaInstall.shouldAutoPrompt()) return;
    this.autoPromptTimer = setTimeout(() => {
      this.autoPromptTimer = null;
      if (this.isModalOpen()) return;
      this.hasAutoPrompted = true;
      this.pwaInstall.open('auto');
    }, PWA_AUTO_PROMPT_DELAY_MS);
  }

  private clearTimer(): void {
    if (this.autoPromptTimer !== null) {
      clearTimeout(this.autoPromptTimer);
      this.autoPromptTimer = null;
    }
  }
}
